// 画布内资产面板（平台演进 §3.5 的"画布内资产面板"露出）
// 账号级资产库在画布里的引用入口——边干活边引用，对标 NemoVideo 的「画布/资产」tab。
// 数据来自 /api/assets（账号级，跨画布跨剧复用），不是画布节点。
import { useState, useEffect, useCallback } from 'react';
import { Search, Plus, X, Loader2, ShieldCheck, ShieldAlert, Clock, ImageIcon, Volume2 } from 'lucide-react';
import api from '../../utils/api';

// 与后端 ASSET_TYPES / §3.5 管理页 tab 对齐
const TYPE_TABS = [
  { key: 'all', label: '全部' },
  { key: 'character', label: '人物' },
  { key: 'scene', label: '场景' },
  { key: 'prop', label: '物品' },
  { key: 'style', label: '风格' },
  { key: 'voice', label: '声线' },
  { key: 'clip', label: '成片' },
];

// 火山入库状态角标（对标 libtv：上传→转圈→打勾）
function VolcBadge({ status }) {
  if (status === 'Active') {
    return (
      <span title="已通过内容审核，可用于 Seedance 生成" className="flex items-center gap-0.5 rounded px-1 py-0.5 text-[10px] text-emerald-600 dark:text-emerald-400">
        <ShieldCheck size={11} /> 已入库
      </span>
    );
  }
  if (status === 'Processing') {
    return (
      <span title="正在审核入库" className="flex items-center gap-0.5 rounded px-1 py-0.5 text-[10px] text-amber-600 dark:text-amber-400">
        <Loader2 size={11} className="animate-spin" /> 审核中
      </span>
    );
  }
  if (status === 'Failed') {
    return (
      <span title="未通过内容审核，无法用于生成" className="flex items-center gap-0.5 rounded px-1 py-0.5 text-[10px] text-rose-600 dark:text-rose-400">
        <ShieldAlert size={11} /> 未通过
      </span>
    );
  }
  return (
    <span title="尚未提交入库" className="flex items-center gap-0.5 rounded px-1 py-0.5 text-[10px] text-stone-400">
      <Clock size={11} /> 未入库
    </span>
  );
}

export default function CanvasAssetPanel({ onClose, onUseAsset }) {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [type, setType] = useState('all');
  const [q, setQ] = useState('');
  const [busyFile, setBusyFile] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const params = {};
      if (type !== 'all') params.type = type;
      if (q.trim()) params.q = q.trim();
      const r = await api.get('/assets', { params });
      setAssets(r.data?.assets || []);
    } catch (e) {
      setErr(e.response?.data?.error || '加载资产失败');
    } finally {
      setLoading(false);
    }
  }, [type, q]);

  // 搜索防抖，避免每个字符打一次接口
  useEffect(() => {
    const t = setTimeout(load, q ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, q]);

  // 提交入库（对应 libtv 图片角落那个图标）
  const submit = async (assetId, fileId) => {
    setBusyFile(fileId);
    try {
      await api.post(`/assets/${assetId}/files/${fileId}/submit`);
      await load();
    } catch (e) {
      setErr(e.response?.data?.error || '入库失败');
    } finally {
      setBusyFile(null);
    }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-[#e4dccf] px-4 py-4 dark:border-white/[0.06]">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg border border-[#ded6c8] bg-white text-stone-500 dark:border-white/[0.08] dark:bg-white/[0.06] dark:text-gray-400">
            <ImageIcon size={15} />
          </div>
          <div className="min-w-0">
            <p className="text-[14px] font-semibold text-stone-800 dark:text-gray-100">资产库</p>
            <p className="text-[11px] text-stone-400">账号级 · 跨画布跨剧复用</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="ml-auto flex h-8 w-8 items-center justify-center rounded-lg text-stone-400 hover:bg-[#eee9de] hover:text-stone-700 dark:hover:bg-white/[0.06]"
            title="关闭资产库"
          >
            <X size={15} />
          </button>
        </div>

        <div className="mt-3 flex items-center gap-2 rounded-lg border border-[#ded6c8] bg-white px-2.5 py-1.5 dark:border-white/[0.08] dark:bg-white/[0.04]">
          <Search size={13} className="shrink-0 text-stone-400" />
          <input
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="搜索资产名称"
            className="min-w-0 flex-1 bg-transparent text-[12px] text-stone-700 outline-none placeholder:text-stone-400 dark:text-gray-300"
          />
        </div>

        <div className="mt-2 flex flex-wrap gap-1">
          {TYPE_TABS.map(t => (
            <button
              key={t.key}
              type="button"
              onClick={() => setType(t.key)}
              className={`rounded-md px-2 py-1 text-[11px] transition-colors ${
                type === t.key
                  ? 'bg-[#6f6196] text-white'
                  : 'text-stone-500 hover:bg-[#eee9de] dark:text-gray-400 dark:hover:bg-white/[0.06]'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3">
        {err && (
          <p className="mb-2 rounded-lg bg-rose-50 px-2.5 py-2 text-[11px] text-rose-700 dark:bg-rose-500/10 dark:text-rose-300">{err}</p>
        )}
        {loading ? (
          <p className="py-10 text-center text-[12px] text-stone-400">加载中…</p>
        ) : !assets.length ? (
          <div className="py-10 text-center">
            <p className="text-[12px] text-stone-400">还没有资产</p>
            <p className="mt-1 text-[11px] text-stone-400">
              在画布上选中图片节点，点「存入资产库」即可沉淀角色/场景，之后跨画布复用
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {assets.map(a => (
              <div
                key={a.id}
                className="rounded-xl border border-[#e4dccf] bg-white px-3 py-2.5 dark:border-white/[0.07] dark:bg-white/[0.03]"
              >
                <div className="flex items-center gap-2">
                  <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-stone-800 dark:text-gray-200">{a.name}</span>
                  <span className="shrink-0 rounded bg-[#f0ebe0] px-1.5 py-0.5 text-[10px] text-stone-500 dark:bg-white/[0.06] dark:text-gray-400">
                    {TYPE_TABS.find(t => t.key === a.type)?.label || a.type}
                  </span>
                </div>
                {a.description && (
                  <p className="mt-1 line-clamp-2 text-[11px] leading-4 text-stone-400">{a.description}</p>
                )}

                {a.files?.length ? (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {a.files.map(f => {
                      const isVoice = a.type === 'voice';
                      return (
                      <div key={f.id} className="w-[84px]">
                        <button
                          type="button"
                          onClick={() => onUseAsset?.(a, f)}
                          title="加到画布上引用"
                          className="group relative block h-[84px] w-[84px] overflow-hidden rounded-lg border border-[#e4dccf] bg-[#faf7f0] dark:border-white/[0.08] dark:bg-white/[0.04]"
                        >
                          {isVoice ? (
                            <span className="flex h-full w-full flex-col items-center justify-center gap-1 text-stone-400">
                              <Volume2 size={20} />
                              {f.data?.voice_id && <span className="text-[9px] text-violet-500">固定声线</span>}
                            </span>
                          ) : f.url ? (
                            <img src={f.url} alt="" className="h-full w-full object-cover" />
                          ) : (
                            <span className="flex h-full w-full items-center justify-center text-stone-300">
                              <ImageIcon size={18} />
                            </span>
                          )}
                          <span className="absolute inset-0 hidden items-center justify-center bg-black/45 text-[11px] text-white group-hover:flex">
                            <Plus size={13} className="mr-0.5" />加到画布
                          </span>
                        </button>
                        {!isVoice && (
                          <div className="mt-1 flex items-center justify-between gap-1">
                            <VolcBadge status={f.volc_status} />
                            {f.volc_status !== 'Active' && (
                              <button
                                type="button"
                                disabled={busyFile === f.id}
                                onClick={() => submit(a.id, f.id)}
                                title="提交火山内容审核入库，通过后才能用于 Seedance 生成"
                                className="shrink-0 rounded px-1 py-0.5 text-[10px] text-[#6f6196] hover:bg-[#efe6fa] disabled:opacity-50 dark:text-[#b9afe7] dark:hover:bg-white/[0.06]"
                              >
                                {busyFile === f.id ? '提交中' : '入库'}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="mt-2 text-[11px] text-stone-400">还没有文件</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
